import React, { useState, useEffect } from 'react';
import API_BASE_URL from './config';

function NotificationSettings({ currentUser, theme, toggleTheme }) {
    const [preferences, setPreferences] = useState({
        email_enabled: true,
        email_address: '',
        days_before_deadline: 3,
        notify_for_statuses: ['pursuing', 'watch'],
        digest_enabled: false,
        digest_frequency: 'daily',
        digest_time: '08:00'
    });
    
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [sendingTest, setSendingTest] = useState(false);

    useEffect(() => {
        loadPreferences();
    }, []);

    const loadPreferences = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/notification_preferences`, {
                credentials: 'include'
            });
            
            if (response.ok) {
                const data = await response.json();
                setPreferences(data);
            }
        } catch (err) {
            console.error('Error loading preferences:', err);
        } finally {
            setLoading(false);
        }
    };

    const savePreferences = async () => {
        setSaving(true);
        setMessage({ text: '', type: '' });

        try {
            const response = await fetch(`${API_BASE_URL}/notification_preferences`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(preferences)
            });

            if (response.ok) {
                setMessage({ text: '✅ Preferences saved successfully!', type: 'success' });
            } else {
                setMessage({ text: '❌ Failed to save preferences', type: 'danger' });
            }
        } catch (err) {
            console.error('Error saving preferences:', err);
            setMessage({ text: '❌ Error saving preferences', type: 'danger' });
        } finally {
            setSaving(false);
        }
    };

    const sendTestEmail = async () => {
        setSendingTest(true);
        setMessage({ text: '', type: '' });

        try {
            const response = await fetch(`${API_BASE_URL}/test_notification`, {
                method: 'POST',
                credentials: 'include'
            });

            const data = await response.json();

            if (data.success) {
                setMessage({ text: '✅ Test email sent! Check your inbox.', type: 'success' });
            } else {
                setMessage({ text: `❌ ${data.message}`, type: 'danger' });
            }
        } catch (err) {
            console.error('Error sending test email:', err);
            setMessage({ text: '❌ Failed to send test email', type: 'danger' });
        } finally {
            setSendingTest(false);
        }
    };

    const handleStatusToggle = (status) => {
        const current = [...preferences.notify_for_statuses];
        const index = current.indexOf(status);
        
        if (index > -1) {
            current.splice(index, 1);
        } else {
            current.push(status);
        }
        
        setPreferences({ ...preferences, notify_for_statuses: current });
    };

    if (loading) {
        return (
            <div className="container mt-4">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2>📧 Email Notifications</h2>
                    <p className="text-muted">Manage your RFQ deadline reminders and digest emails</p>
                </div>
                <button 
                    onClick={toggleTheme} 
                    className="theme-toggle"
                    title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
                    aria-label="Toggle theme"
                >
                    {theme === 'light' ? '🌙' : '☀️'}
                </button>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
                    {message.text}
                    <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setMessage({ text: '', type: '' })}
                        aria-label="Close"
                    ></button>
                </div>
            )}

            <div className="card mb-4">
                <div className="card-body">
                    <h5 className="card-title">⏰ Deadline Reminders</h5>
                    <p className="card-text text-muted">
                        Get notified when RFQs you're tracking are due soon
                    </p>

                    <div className="form-check form-switch mb-3">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="emailEnabled"
                            checked={preferences.email_enabled}
                            onChange={(e) => setPreferences({ ...preferences, email_enabled: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="emailEnabled">
                            <strong>Enable deadline reminder emails</strong>
                        </label>
                    </div>

                    {preferences.email_enabled && (
                        <>
                            <div className="mb-3">
                                <label htmlFor="emailAddress" className="form-label">
                                    Email Address <span className="text-muted">(optional - defaults to your account email)</span>
                                </label>
                                <input
                                    type="email"
                                    className="form-control"
                                    id="emailAddress"
                                    placeholder={currentUser?.email || 'your-email@example.com'}
                                    value={preferences.email_address || ''}
                                    onChange={(e) => setPreferences({ ...preferences, email_address: e.target.value })}
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="daysBeforeDeadline" className="form-label">
                                    Send reminder <strong>{preferences.days_before_deadline} day{preferences.days_before_deadline !== 1 ? 's' : ''}</strong> before due date
                                </label>
                                <input
                                    type="range"
                                    className="form-range"
                                    id="daysBeforeDeadline"
                                    min="1"
                                    max="7"
                                    value={preferences.days_before_deadline}
                                    onChange={(e) => setPreferences({ ...preferences, days_before_deadline: parseInt(e.target.value) })}
                                />
                                <div className="d-flex justify-content-between text-muted small">
                                    <span>1 day</span>
                                    <span>7 days</span>
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Notify me for jobs with these statuses:</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {['pursuing', 'watch', 'new', 'completed'].map(status => (
                                        <div key={status} className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`status-${status}`}
                                                checked={preferences.notify_for_statuses.includes(status)}
                                                onChange={() => handleStatusToggle(status)}
                                            />
                                            <label className="form-check-label" htmlFor={`status-${status}`}>
                                                {status.charAt(0).toUpperCase() + status.slice(1)}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="card mb-4">
                <div className="card-body">
                    <h5 className="card-title">📊 Daily Digest</h5>
                    <p className="card-text text-muted">
                        Get a morning email with all your active RFQs
                    </p>

                    <div className="form-check form-switch mb-3">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="digestEnabled"
                            checked={preferences.digest_enabled}
                            onChange={(e) => setPreferences({ ...preferences, digest_enabled: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="digestEnabled">
                            <strong>Enable daily digest emails</strong>
                        </label>
                    </div>

                    {preferences.digest_enabled && (
                        <>
                            <div className="mb-3">
                                <label htmlFor="digestTime" className="form-label">
                                    Send digest at:
                                </label>
                                <input
                                    type="time"
                                    className="form-control"
                                    id="digestTime"
                                    value={preferences.digest_time}
                                    onChange={(e) => setPreferences({ ...preferences, digest_time: e.target.value })}
                                />
                                <div className="form-text">
                                    Daily digest will be sent every morning at this time
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="d-flex gap-2 mb-4">
                <button 
                    className="btn btn-primary" 
                    onClick={savePreferences}
                    disabled={saving}
                >
                    {saving ? 'Saving...' : '💾 Save Preferences'}
                </button>

                <button 
                    className="btn btn-outline-secondary" 
                    onClick={sendTestEmail}
                    disabled={sendingTest || !preferences.email_enabled}
                >
                    {sendingTest ? 'Sending...' : '📧 Send Test Email'}
                </button>
            </div>

            <div className="card bg-light">
                <div className="card-body">
                    <h6 className="card-title">ℹ️ How it works</h6>
                    <ul className="mb-0">
                        <li>Deadline reminders are sent once per day for jobs due on the target date</li>
                        <li>Daily digest includes all active jobs, grouped by urgency</li>
                        <li>Only jobs with selected statuses will trigger reminders</li>
                        <li>You can disable notifications anytime by unchecking "Enable" above</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default NotificationSettings;

